"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export type Tag = {
  id: string;
  label: string;
  interest: string;
};

type TagsSelectorProps = {
  tags: Tag[];
  selectedTags: Tag[];
  onChange?: (selected: Tag[]) => void;
  onRemoveTag?: (tag: Tag) => void;
  className?: string;
};

export function TagsSelector({ tags, selectedTags, onChange, onRemoveTag, className }: TagsSelectorProps) {
  const selectedsContainerRef = useRef<HTMLDivElement>(null);
  const trendingBoxRef = useRef<HTMLDivElement>(null);
  const [initialTrendingMinHeight, setInitialTrendingMinHeight] = useState<number | undefined>(undefined);
  const isMobile = useIsMobile();

  const removeSelectedTag = (id: string) => {
    const tagToRemove = selectedTags.find(tag => tag.id === id);
    if (tagToRemove && onRemoveTag) {
      onRemoveTag(tagToRemove);
    } else if (onChange) {
      onChange(selectedTags.filter((tag) => tag.id !== id));
    }
  };

  const addSelectedTag = (tag: Tag) => {
    if (onChange)
      onChange([...selectedTags, tag]);
  };

  useEffect(() => {
    if (selectedsContainerRef.current) {
      selectedsContainerRef.current.scrollTo({
        left: selectedsContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  }, [selectedTags]);

  useEffect(() => {
    if (trendingBoxRef.current && initialTrendingMinHeight === undefined) {
      setInitialTrendingMinHeight(trendingBoxRef.current.offsetHeight);
    }
  }, [initialTrendingMinHeight]);

  return (
    <div className={`w-full flex flex-col ${className}`}>
      {tags.length > selectedTags.length && (
        <>
          <p className="text-sm text-green-900 mb-2 text-center">Need ideas? Browse trending topics:</p>
          <motion.div
            ref={trendingBoxRef}
            className="bg-white/70 backdrop-blur-[2.5px] shadow-sm p-2 border w-full"
            style={{
              borderRadius: 16,
              ...(isMobile ? {} : initialTrendingMinHeight ? { minHeight: initialTrendingMinHeight } : {}),
            }}
            layout
          >
            <motion.div className="flex flex-wrap gap-2">
              {tags
                .filter(
                  (tag) =>
                    !selectedTags.some((selected) => selected.id === tag.id)
                )
                .map((tag) => (
                  <motion.button
                    key={tag.id}
                    layoutId={`tag-${tag.id}`}
                    className="flex items-center gap-1 px-4 py-2.5 bg-gray-100/60 rounded-full shrink-0 shadow"
                    onClick={() => addSelectedTag(tag)}
                    style={{
                      borderRadius: 14,
                    }}
                    type="button"
                  >
                    <motion.span
                      layoutId={`tag-${tag.id}-label`}
                      className="text-gray-700 font-medium"
                    >
                      {tag.label}
                    </motion.span>
                  </motion.button>
                ))}
            </motion.div>
          </motion.div>
        </>
      )}
      <p className="text-base text-green-900 mb-2 mt-4 text-center">Your Selected Topics</p>
      <motion.div
        className="w-full flex flex-wrap items-start gap-1.5 border min-h-12 mb-2 p-1.5 bg-white/70 backdrop-blur-[2.5px]"
        style={{
          borderRadius: 16,
        }}
        ref={selectedsContainerRef}
        layout
      >
        {selectedTags.map((tag) => (
          <motion.div
            key={tag.id}
            className="flex items-center gap-1 pl-3 pr-1 py-1 bg-white/70 backdrop-blur-[2.5px] shadow border h-8 shrink-0"
            style={{
              borderRadius: 14,
            }}
            layoutId={`tag-${tag.id}`}
          >
            <motion.span
              layoutId={`tag-${tag.id}-label`}
              className="text-gray-700 font-medium"
            >
              {tag.label}
            </motion.span>
            <button
              onClick={() => removeSelectedTag(tag.id)}
              className="p-1 rounded-full"
              type="button"
            >
              <X className="size-5 text-gray-500" />
            </button>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
